// Chakra imports
import {
  Box, Text, Flex,
  FormControl, Input,
  Link, Icon, useColorModeValue,
  Modal, ModalOverlay,
  ModalContent, ModalHeader,
  ModalFooter, ModalBody,
  ModalCloseButton, Button, useDisclosure,
  Menu, MenuButton, MenuList, MenuItem
} from "@chakra-ui/react";
// Custom components
import Card from "components/card/Card.js";
import useAuthentication from "providers/Authentication";
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';

import { MdEdit } from "react-icons/md";
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import constants from "providers/Constants";

export default function Information(props) {

  const { title, value, disabled, can_verify, date, upload, upload_array, verification_type,
    select, hint, select_array, onChange, length, input_type, data, ...rest } = props;
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // For searchable select
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();
  // Chakra Color Mode
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = "gray.400";
  const bg = useColorModeValue("white", "navy.700");
  // For search input + menu (dark mode support)
  const searchBg = useColorModeValue('white', 'gray.700');
  const searchColor = useColorModeValue('gray.800', 'white');
  const searchBorder = useColorModeValue('#E2E8F0', 'gray.600');
  const menuBg = useColorModeValue('white', 'navy.700');
  const menuColor = useColorModeValue('secondaryGray.900', 'white');
  const menuBorder = useColorModeValue('#E2E8F0', 'gray.600');
  const placeholderColor = useColorModeValue('gray.500', 'whiteAlpha.700');
  const menuHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');

  const authentication = useAuthentication();
  const [verification_id, setVerificationId] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();

  const isMissing = !disabled && (!value || value === 'N/A' || (typeof value === 'string' && value.trim() === ''));

  const isVerifiable = can_verify && value !== 'N/A';
  const SELECT_STYLE = {
    width: '100%',
    padding: '10px',
    marginBottom: '24px',
    borderRadius: '15px',
    borderColor: '#E2E8F0',
    borderWidth: '1px',
    fontSize: 'sm',
    fontWeight: '500',
  };
  const handleSave = () => {
    setLoading(true);
    onClose();
    onChange(input);
    setLoading(false);
  };

  // Render field
  const renderFieldControl = () => {
    if (select) {
      const filteredOptions = select_array ? select_array.filter(item =>
        item.toLowerCase().includes(search.toLowerCase())
      ) : [];
      return null;
    }
    if (upload) {
      return (
        <select
          required
          defaultValue=""
          onChange={(e) => setInput(e.target.value)}
          style={SELECT_STYLE}
        >
          <option value="" disabled>{t('Choose')}</option>
          {upload_array && upload_array.map((item, i) => (
            <option key={i} value={item.value}>{item.name}</option>
          ))}
        </select>
      );
    }
    if (date) {
      return (
        <Input
          isRequired
          variant='auth'
          fontSize='sm'
          type='date'
          onChange={e => {
            const formattedDate = new Date(e.target.value).toISOString().split('T')[0].replace(/-/g, '/');
            setInput(formattedDate);
          }}
          placeholder={value}
          mb='24px'
          fontWeight='500'
          size='lg'
        />
      );
    }
    return (
      <Input
        isRequired
        variant='auth'
        fontSize='sm'
        type={input_type || 'text'}
        maxLength={length}
        onChange={e => setInput(e.target.value)}
        placeholder={value}
        mb='24px'
        fontWeight='500'
        size='lg'
      />
    );
  };

  return (
    <Card bg={bg} {...rest}>
      <Flex align='center' direction={{ base: "column", md: "row" }}>
        <Box maxW='100%'>
          <Flex align='center'>
            <Text fontWeight='500' color={textColorSecondary} fontSize='sm' isTruncated>
              {title}
            </Text>
            {isMissing && (
              <Icon
                as={AiOutlineExclamationCircle}
                color='red.400'
                ml='6px'
                boxSize='14px'
                title={t('This field is required')}
                aria-label={t('This field is required')}
              />
            )}
          </Flex>
          <Text color={textColorPrimary} fontWeight='500' fontSize='md' isTruncated>
            {value}
          </Text>
        </Box>
        <Flex ml={{ base: 0, md: 'auto' }} mt={{ base: 2, md: 0 }} align='center' gap={4}>
          {isVerifiable && (
            <Link variant='no-hover' p='0px !important' onClick={onOpenVerificationModal} aria-label={t('Verify')}>
              <Text fontSize='sm' color={brandStars} fontWeight='700'>
                {t('VERIFY')}
              </Text>
            </Link>
          )}
          {!disabled && (
            <Link variant='no-hover' p='0px !important' onClick={onOpen} aria-label={t('Edit')}>
              <Icon as={MdEdit} color='secondaryGray.500' h='18px' w='18px' />
            </Link>
          )}
        </Flex>

        {/* Input Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('Edit {{title}}', { title })}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl>
                {select ? (
                  (() => {
                    const filteredOptions = select_array ? select_array.filter(item =>
                      item.toLowerCase().includes(search.toLowerCase())
                    ) : [];
                    return (
                      <Menu isOpen={menuOpen} onClose={() => setMenuOpen(false)}>
                        <MenuButton
                          as={Box}
                          onClick={() => setMenuOpen(!menuOpen)}
                          mb="24px"
                          size="lg"
                          px={4}
                          py={2}
                          borderWidth="1px"
                          borderRadius="md"
                          borderColor={menuBorder}
                          fontSize="md"
                          fontWeight="500"
                          textAlign="left"
                          w="100%"
                          bg={menuBg}
                          color={menuColor}
                          _hover={{ bg: menuBg }}
                          _active={{ bg: menuBg }}
                          style={{ cursor: 'pointer' }}
                        >
                          {input || t('Choose')}
                        </MenuButton>
                        <MenuList
                          minW="240px"
                          maxH="260px"
                          overflowY="auto"
                          px={2}
                          bg={menuBg}
                          color={menuColor}
                          borderColor={menuBorder}
                        >
                          <Input
                            placeholder={t('Search...')}
                            mb="10px"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            size="sm"
                            autoFocus
                            bg={searchBg}
                            color={searchColor}
                            borderColor={searchBorder}
                            _placeholder={{ color: placeholderColor }}
                          />
                          {filteredOptions.length === 0 && (
                            <MenuItem isDisabled bg={menuBg} color={menuColor} _hover={{ bg: menuBg }}>
                              {t('No results found')}
                            </MenuItem>
                          )}
                          {filteredOptions.map((item) => (
                            <MenuItem
                              key={item}
                              onClick={() => {
                                setInput(item);
                                setMenuOpen(false);
                              }}
                              isSelected={input === item}
                              bg={menuBg}
                              color={menuColor}
                              _hover={{ bg: menuHoverBg }}
                            >
                              {item}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    );
                  })()
                ) : renderFieldControl()}
                {hint ? (
                  <Text fontSize='xs' color={'red'} ml='5px'>
                    {hint}.
                  </Text>
                ) : null}
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={onClose} variant='ghost' fontWeight='500'>
                {t('Close')}
              </Button>
              <Button
                isLoading={loading}
                isDisabled={loading || !input}
                onClick={handleSave}
                variant='brand'
                fontWeight='500'>
                {t('Save')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Verification modal */}
        <Modal isOpen={isModalOpen} onClose={onModalClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t("Verify '{{value}}'", { value })} </ModalHeader>
            <ModalCloseButton />
            <ModalBody>

              <FormControl>
                <Input
                  isRequired
                  variant='auth'
                  fontSize='sm'
                  type='text'
                  maxLength={length}
                  ms={{ base: "0px", md: "0px" }}
                  onChange={e => setInput(e.target.value)}
                  placeholder={t('One Time Passcode')}
                  mb='10px'
                  fontWeight='500'
                  size='lg'
                />
                {loading && (
                  <Text fontSize='xs' color={'orange'} ml='5px'>
                    {t('Your request is processing, Please wait...')}
                  </Text>
                )}
                {!loading && verification_id && (
                  <Text fontSize='xs' color={'green'} ml='5px'>
                    {t('Enter the One Time Passcode (OTP) sent to linked phone or email')}
                  </Text>
                )}
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Button
                me='100%'
                mb='10px'
                w='140px'
                mr={3} onClick={onModalClose}
                minW='140px'
                mt={{ base: "20px", "2xl": "auto" }}
                variant='ghost'
                fontWeight='500'>
                {t('Cancel')}
              </Button>

              {(loading || (verification_id)) && (
                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  onClick={() => {
                    verifyVerificationCode(verification_type, verification_id, input);
                  }}
                  variant='brand'
                  fontWeight='500'>
                  {t('Proceed')}
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    </Card>
  );

  // Request verification code
  function requestVerificationCode(verification_type, params = {}) {
    setLoading(true);
    authentication.requestVerificationCode({
      action: 'request',
      verification_type: verification_type,
      [verification_type]: value,
      ...params
    }, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setVerificationId(data?.verification_id);
        constants.showToast(
          "success",
          t('SUCCESS'),
          t('Verification code sent successfully')
        );
      }
    });
  }

  // Verify verification code
  function verifyVerificationCode(verification_type, verification_id, input_otp) {
    setLoading(true);
    authentication.requestVerificationCode({
      action: 'verify',
      verification_type: verification_type,
      [verification_type]: value,
      verification_id: verification_id,
      otp: input_otp
    }, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        constants.showToast(
          "success",
          t('SUCCESS'),
          t('Verification successful')
        );
        onModalClose();
        window.location.reload();
      }
    });
  }

  // Open verification modal
  function onOpenVerificationModal() {
    if (isModalOpen) { onModalClose(); }
    else {
      onModalOpen();
      requestVerificationCode(verification_type, verification_type != 'email' ? { country_code: data?.country_code, phone: data?.phone } : null);
    }
  }
}
